import { feedKey } from '../lib/feedContext';
/**
 * Списки выбора: что этот контур реально показывал.
 *
 * Сито и студия собирали свои варианты (конторы, дисциплины) из ТЕКУЩЕГО кадра
 * ленты. Замысел был правильный — не показывать сорок контор, из которых в ленте
 * нет тридцати восьми, — но на живом контуре он обернулся неработоспособной
 * настройкой: кадр приходит каждую секунду, конторы то появляются, то пропадают,
 * а сортировка по числу вилок ещё и переставляет их местами. Человек тянется к
 * кнопке, а под курсором уже другая (08.08, тестер).
 *
 * Своя контора — это не свойство рынка сию секунду, а свойство человека: где у
 * него открыт счёт. Такой набор собирают один раз и надолго, и он обязан
 * пережить и тихий час, когда у конторы нет ни одной вилки, и перезагрузку
 * вкладки. Отсюда правило: код, увиденный хоть раз, из списка не убираем; ноль
 * рядом с именем — подпись, а не запрет.
 *
 * Порядок при этом фиксированный (по алфавиту), а не по числу вилок: «умная»
 * сортировка меняла бы раскладку кнопок на каждом кадре — ровно та беда, от
 * которой этот модуль и написан.
 *
 * ЧЕЙ ЭТО СПИСОК. Хранилище вкладки общее на весь домен, а контуров на домене
 * два, и ленты у них разные: у зеркала полсотни чужих контор, у нашей ленты —
 * свои адаптеры. Один ключ на оба означал, что в сите v1 стояли bet365,
 * ladbrokes и ещё полсотни имён, которых наш движок не отдаёт и отдать не может
 * (12.08, владелец — «убрать мусор по типу букмекеров, которых в ленте вообще
 * нет»). Выбор из семидесяти кнопок, шестьдесят из которых мертвы, — это не
 * настройка, а работа по отсеиванию, переложенная на человека. Ключ поэтому
 * назван контуром: список v1 собирается только из ленты v1.
 *
 * СКОЛЬКО ДЕРЖИМ. Вечно — нельзя: адаптер отключают, донор меняет состав, и
 * список медленно зарастает именами, которых больше не бывает. Контору без
 * вилки двое суток убираем (11.10, владелец: баффбет и хвост, которого в ленте
 * нет). Дисциплина живёт месяц: тихий день футбола — не повод прятать фишку.
 */

import { sportCanon } from '../lib/sports';
import { API_BASE } from '../lib/build';
import { isSourceCode } from './mirrorDesk';
import { parked } from './parked';
import { sieveDropsBook, venueCanon } from './venues';

/** Коды из `meta.sources`. Пока не заданы — список как раньше (тесты, холодный старт). */
let liveBookCodes: Set<string> | null = null;

/** Кнопки контор только из линии, не из справочника на 43 имени. */
export function constrainLiveBooks(codes: readonly string[] | null): void {
  liveBookCodes = codes === null ? null : new Set(
    codes.map((code) => venueCanon(code)).filter((code) => code !== ''),
  );
}

/** Что именно копим. */
export type Kind = 'books' | 'sports';

/**
 * Потолок: не бизнес-правило, а защита от мусора. Если в ленту когда-нибудь
 * попадёт битый код ноги, список не должен расти бесконечно и забивать
 * хранилище вкладки. Вытесняются самые давние по встрече.
 */
const MAX = 200;

/** Контора без вилки дольше двух дней — из сита уходит. Слово владельца 11.10. */
export const KEEP_DAYS_BOOKS = 2;
/** Дисциплина живёт дольше: тихий день спорта — не повод прятать футбол. */
export const KEEP_DAYS_SPORTS = 30;
/** @deprecated окно контор; тесты и старые импорты. */
export const KEEP_DAYS = KEEP_DAYS_BOOKS;

/** Имя контура для ключа: '' у боевой ленты, '/v2' у зеркала. */
const CONTOUR = API_BASE === '' ? 'v1' : API_BASE.replace(/[^a-z0-9]+/gi, '') || 'alt';

/** Ключ хранилища. Открыт наружу ради тестов: подделывать имя руками негде. */
export const slotKey = (kind: Kind): string => feedKey(`forq.seen.${kind}.${CONTOUR}`);

/**
 * Ключ прежнего, общего на оба контура списка. Снимается при первом же чтении:
 * в нём лежит именно та смесь двух лент, ради которой всё и переписано, и
 * оставить её означало бы хранить мусор, который уже никто не читает.
 */
const legacyKey = (kind: Kind): string => feedKey(`forq.seen.${kind}`);

/** Код → когда его видели в последний раз, мс эпохи. */
type Ledger = Record<string, number>;

function recanonBooks(ledger: Ledger): Ledger {
  const out: Ledger = {};
  for (const [raw, at] of Object.entries(ledger)) {
    const code = venueCanon(raw);
    if (code === '' || isSourceCode(code) || sieveDropsBook(code)) continue;
    const prev = out[code];
    out[code] = prev === undefined ? at : Math.max(prev, at);
  }
  return out;
}

function read(kind: Kind): Ledger {
  try {
    localStorage.removeItem(legacyKey(kind));
    const raw = localStorage.getItem(slotKey(kind));
    if (raw === null) return {};
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out: Ledger = {};
    for (const [code, at] of Object.entries(parsed as Record<string, unknown>)) {
      if (code !== '' && typeof at === 'number' && Number.isFinite(at)) out[code] = at;
    }
    if (kind !== 'books') return out;
    const clean = recanonBooks(out);
    if (JSON.stringify(Object.keys(clean).sort()) !== JSON.stringify(Object.keys(out).sort())) {
      write(kind, clean);
    }
    return clean;
  } catch {
    // Битое хранилище — не причина остаться вовсе без списка: копим заново.
    return {};
  }
}

function write(kind: Kind, ledger: Ledger): void {
  try {
    localStorage.setItem(slotKey(kind), JSON.stringify(ledger));
  } catch {
    // Переполненное или запрещённое хранилище: в этой вкладке список всё равно
    // соберётся из кадров, просто не переживёт перезагрузку.
  }
}

/**
 * Отметить встреченное в кадре и вернуть весь список — по алфавиту.
 *
 * `now` передаётся снаружи ради тестов: время здесь решает, что из списка
 * выпадет, и проверять это, дожидаясь месяца, нечем.
 */
export function remember(kind: Kind, seen: Iterable<string>, now: number = Date.now()): string[] {
  const ledger = read(kind);
  const before = JSON.stringify(ledger);

  for (const raw of seen) {
    if (typeof raw !== 'string' || raw === '') continue;
    const code = kind === 'books' ? venueCanon(raw) : kind === 'sports' ? sportCanon(raw) : raw;
    if (code === '') continue;
    if (kind === 'books' && (isSourceCode(code) || sieveDropsBook(code))) continue;
    if (kind === 'books' && parked.ghostVenues && liveBookCodes !== null && !liveBookCodes.has(code)) {
      continue;
    }
    ledger[code] = now;
  }
  if (kind === 'books' && parked.ghostVenues && liveBookCodes !== null) {
    for (const code of Object.keys(ledger)) {
      if (!liveBookCodes.has(code)) delete ledger[code];
    }
  }
  if (kind === 'books') {
    for (const code of Object.keys(ledger)) {
      if (isSourceCode(code) || sieveDropsBook(code)) delete ledger[code];
    }
  }
  const keepMs = (kind === 'books' ? KEEP_DAYS_BOOKS : KEEP_DAYS_SPORTS) * 24 * 60 * 60 * 1000;
  for (const [code, at] of Object.entries(ledger)) {
    if (now - at > keepMs) delete ledger[code];
  }

  let codes = Object.keys(ledger).sort();
  if (codes.length > MAX) {
    // Вытесняем самые давние, а не хвост алфавита: выпасть должно забытое, а не
    // всё, что начинается на «w».
    const stale = codes.sort((a, b) => (ledger[a] ?? 0) - (ledger[b] ?? 0)).slice(0, codes.length - MAX);
    for (const code of stale) delete ledger[code];
    codes = Object.keys(ledger).sort();
  }

  if (JSON.stringify(ledger) !== before) write(kind, ledger);
  return codes;
}

/** Забыть накопленное. Нужно тестам и сбросу настроек; в ленте не зовётся. */
export function forget(kind: Kind): void {
  try {
    localStorage.removeItem(slotKey(kind));
    localStorage.removeItem(legacyKey(kind));
  } catch {
    // Нечего забывать.
  }
}
